import { Checkbox } from "@headlessui/react";
import { CheckIcon } from "@heroicons/react/24/solid";
import { getBonzahProducts } from "api/bonzah/bonzah";
import { tripModificationRequest } from "api/booking/booking";
import { useData } from "data/data-provider";
import BonzahAcknowledgement from "designs/Common/BonzahAcknowledgement/BonzahAcknowledgement";
import { globalJson } from "global/global_json";
import moment from "moment";
import React, { FC, useEffect, useRef, useState } from "react";
import toast from "react-hot-toast";
import Badge from "shared/Badge/Badge";

interface InsuranceandExtrasProp {
  bookingDetails: any;
  // 1NOW-1508: owned by EditBooking — refetches the Bonzah catalog for a
  // package and reconciles the selection against it.
  fetchBonzahProducts: (selectedType?: string) => void;
}

const InsuranceandExtras: FC<InsuranceandExtrasProp> = ({
  bookingDetails,
  fetchBonzahProducts,
}) => {
  const {
    setSelectedBonzahProducts,

    setSelectedExtras,
    bookingdetails,
    selectedExtras,
    selectedCar,
    selectedBonzahProducts,
    bonzahProducts,
    startDate,
    endDate,
    startTime,
    endTime,
    is_long_term_rental,
    long_term_rental_nature,
  } = useData();
  const [loading, setLoading] = useState(false);

  // 1NOW-688: the renter can now change/contract Bonzah coverage on the website
  // edit flow. Selecting a Bonzah coverage requires the same one-time
  // acknowledgement as the create/booking flow.
  const [bonzahAckOpen, setBonzahAckOpen] = useState(false);
  const [bonzahAcknowledged, setBonzahAcknowledged] = useState(false);
  const [pendingBonzahProduct, setPendingBonzahProduct] = useState<any>(null);

  const _tripEnd =
    endDate && endTime
      ? moment(
          `${moment(endDate).format("YYYY-MM-DD")} ${endTime}`,
          "YYYY-MM-DD HH:mm"
        )
      : null;
  // 1NOW-1421: Bonzah cannot cover elapsed days. When the whole (edited) booking
  // is in the past — drop-off date before today — block Bonzah coverages +
  // add-ons and show an error; other (canopy/manual) options stay available.
  const allDaysInPast = _tripEnd
    ? _tripEnd.isBefore(moment().startOf("day"), "day")
    : false;
  // 1NOW-1437: an already-STARTED booking with purchased Bonzah being
  // contracted below today is NOT an "all past" booking — the live policy gets
  // contracted until today (the bonzah_alerts popup on Update explains this).
  // Stripping the codes here would divert the backend from the contraction
  // branch to an options-change/removal branch and kill that popup, so the
  // 1NOW-1421 strip + "cannot be added" error only apply when nothing was
  // purchased (a genuinely past booking can't ADD coverage).
  const ongoingPurchasedBonzah =
    !!bookingDetails?.has_bonzah_insurance &&
    !!bookingDetails?.bonzah_purchased &&
    !!bookingDetails?.pick_up_time &&
    moment(bookingDetails.pick_up_time).isBefore(moment());
  const stripPastBonzah = allDaysInPast && !ongoingPurchasedBonzah;

  // Everything is stored as a FLAT array of coverage codes in
  // selectedBonzahProducts (1NOW-1206) and submitted as-is. Cast on write to
  // satisfy the Set-typed setter (BookingDetailsPage seeds it the same way).
  const currentSelection = (): any[] =>
    Array.isArray(selectedBonzahProducts) ? selectedBonzahProducts : [];

  const productValues = (product: any): any[] =>
    Array.isArray(product?.value) ? product.value : [product?.value];

  // 1NOW-1053: Bonzah primary packages share codes (Standard = ["cdw","rcli"],
  // Minimum = ["cdw"], and the RCLI add-on is ["rcli"]), so a plain `some()`
  // test can't tell them apart — Standard could never be unchecked, blocking a
  // switch to Minimum. Resolve the SELECTED primary as the Bonzah package whose
  // full code set is contained in the selection; the largest match wins.
  const bonzahPrimaryProducts = (
    bonzahProducts?.insurance_options ?? []
  ).filter((p: any) => p?.insurance_by_bonzah === true);

  const selectedPrimaryProduct = (): any => {
    const sel = currentSelection();
    let best: any = null;
    let bestLen = 0;
    for (const p of bonzahPrimaryProducts) {
      const vals = productValues(p);
      if (
        vals.length > 0 &&
        vals.every((v: any) => sel.includes(v)) &&
        vals.length > bestLen
      ) {
        best = p;
        bestLen = vals.length;
      }
    }
    return best;
  };

  const isSamePrimary = (a: any, b: any) =>
    !!a &&
    !!b &&
    JSON.stringify(productValues(a)) === JSON.stringify(productValues(b));

  // Checkbox state: a Bonzah primary is "checked" only when it is THE selected
  // primary (exact, largest-subset); manual insurance uses a plain membership
  // test (multi-select, 1NOW-710).
  const isProductChecked = (product: any) => {
    if (product?.insurance_by_bonzah === true) {
      return isSamePrimary(selectedPrimaryProduct(), product);
    }
    return (
      Array.isArray(selectedBonzahProducts) &&
      productValues(product).some((v: any) => currentSelection().includes(v))
    );
  };

  // 1NOW-710/1NOW-1053: manual (non-Bonzah) insurance is multi-select "like
  // extras"; Bonzah primary coverage is single-select. Switching the primary
  // package replaces the previous primary's codes but preserves any add-ons the
  // renter explicitly selected (1NOW-1206). Bonzah and manual are mutually
  // exclusive.
  const applyBonzahProductSelection = (product: any) => {
    const values: any[] = productValues(product);
    const selected = currentSelection();

    if (product?.insurance_by_bonzah === true) {
      if (isSamePrimary(selectedPrimaryProduct(), product)) {
        // Deselect: drop all Bonzah primary + add-on codes, keep manual.
        setSelectedBonzahProducts(
          selected.filter(
            (c: any) =>
              !bonzahCoverageValues.has(c) && !bonzahAddonValues.has(c)
          ) as any
        );
      } else {
        // 1NOW-1206: switching the primary Bonzah package keeps the add-ons the
        // renter explicitly added (PAI/SLI/RCLI apply on top of either package).
        // Only the previous primary's own codes are replaced — a code that was
        // part of the old package's bundle (e.g. Standard's bundled RCLI) is not
        // carried over as a standalone add-on. Manual insurance is dropped
        // (mutually exclusive with Bonzah).
        const prevPrimaryValues = productValues(selectedPrimaryProduct());
        const explicitAddons = selected.filter(
          (c: any) =>
            bonzahAddonValues.has(c) && !prevPrimaryValues.includes(c)
        );
        setSelectedBonzahProducts(
          Array.from(new Set([...values, ...explicitAddons])) as any
        );
        // 1NOW-1508: the add-on catalog is package-specific (SLI is offered
        // only under Standard, the standalone RCLI add-on only under Minimum),
        // so refetch it for the NEW package — like the create flow's
        // handleBonzahProductSelection. fetchBonzahProducts also drops any
        // carried-over add-on the new package doesn't offer (SLI under
        // Minimum) while keeping compatible ones like PAI (1NOW-1206).
        if (product?.title) {
          fetchBonzahProducts(product.title.toLowerCase());
        }
      }
    } else {
      // Manual / non-Bonzah insurance: multi-select toggle. Selecting one drops
      // any Bonzah primary + add-ons (Bonzah and manual are mutually exclusive).
      const isSel = values.some((v) => selected.includes(v));
      if (isSel) {
        setSelectedBonzahProducts(
          selected.filter((c: any) => !values.includes(c)) as any
        );
      } else {
        const manualOnly = selected.filter(
          (c: any) => !bonzahCoverageValues.has(c) && !bonzahAddonValues.has(c)
        );
        setSelectedBonzahProducts([...manualOnly, ...values] as any);
      }
    }
  };

  // 1NOW-710: Bonzah add-ons (e.g. PAI/SLI/RCLI) toggle independently on top of
  // the selected primary Bonzah coverage.
  const toggleBonzahAddon = (addon: any) => {
    const values: any[] = productValues(addon);
    const selected = currentSelection();
    const isSel = values.some((v) => selected.includes(v));
    setSelectedBonzahProducts(
      (isSel
        ? selected.filter((c: any) => !values.includes(c))
        : [...selected, ...values]) as any
    );
  };

  const attemptBonzahProductSelection = (product: any) => {
    const isCurrentlySelected =
      product?.insurance_by_bonzah === true
        ? isSamePrimary(selectedPrimaryProduct(), product)
        : productValues(product).some((v: any) =>
            currentSelection().includes(v)
          );
    // Selecting (not deselecting) a Bonzah coverage needs the acknowledgement.
    if (
      product?.insurance_by_bonzah === true &&
      !isCurrentlySelected &&
      !bonzahAcknowledged
    ) {
      setPendingBonzahProduct(product);
      setBonzahAckOpen(true);
      return;
    }
    applyBonzahProductSelection(product);
  };

  const confirmBonzahAcknowledgement = () => {
    setBonzahAcknowledged(true);
    setBonzahAckOpen(false);
    if (pendingBonzahProduct) {
      applyBonzahProductSelection(pendingBonzahProduct);
      setPendingBonzahProduct(null);
    }
  };

  const renderDescriptionWithLink = (description: string) => {
    const urlRegex = /(https?:\/\/[^\s]+)/g;
    const parts = description.split(urlRegex);
    return parts.map((part, index) => {
      if (urlRegex.test(part)) {
        return (
          <a
            key={index}
            href={part}
            target="_blank"
            rel="noopener noreferrer"
            className="text-blue-500 hover:underline"
          >
            {part}
          </a>
        );
      }
      return <span key={index}>{part}</span>;
    });
  };

  const handleExtraChange = (extraId: any, checked: any) => {
    setSelectedExtras((prevSelectedExtras) => {
      const updatedExtras = new Set(prevSelectedExtras);
      if (checked) {
        updatedExtras.add(extraId);
      } else {
        updatedExtras.delete(extraId);
      }
      return updatedExtras;
    });
  };

  // 1NOW-1206 / 1NOW-1053 / 1NOW-714: Bonzah coverages are string codes
  // ("cdw", "rcli", "pai", "sli", ...), but a fleet's allowed_insurances holds
  // numeric option IDs — the Bonzah block is 99991-99995. Comparing a code
  // against the ID list always "fails", which (a) silently stripped Bonzah from
  // the selection whenever the car was (re)set on an edit/extension and (b)
  // raised a false "insurance unavailable, choose new options" banner on a fleet
  // that actually allows Bonzah. Resolve Bonzah membership against the Bonzah
  // block / "all"; keep the numeric-ID rule for every other insurance.
  const BONZAH_INSURANCE_IDS = [99991, 99992, 99993, 99994, 99995];
  const bonzahCoverageValues = new Set(
    (bonzahProducts?.insurance_options ?? [])
      .filter((product: any) => product?.insurance_by_bonzah === true)
      .flatMap((product: any) =>
        Array.isArray(product?.value) ? product.value : [product?.value]
      )
  );
  // 1NOW-710: Bonzah add-on coverages (their own codes) are also Bonzah, so they
  // resolve against the Bonzah block below and the 1NOW-1206 strip-effect never
  // drops them from the selection on a Bonzah-allowed fleet.
  const bonzahAddonValues = new Set(
    (bonzahProducts?.addons ?? []).flatMap((addon: any) =>
      Array.isArray(addon?.value) ? addon.value : [addon?.value]
    )
  );
  // A fleet with no allowed_insurances list (not an array) is treated as
  // unrestricted; an explicit list is checked against "all" / the Bonzah block.
  const hasAllowedList = Array.isArray((selectedCar as any)?.allowed_insurances);
  const allowedInsurances: any[] = hasAllowedList
    ? (selectedCar as any).allowed_insurances
    : [];
  const fleetAllowsBonzah =
    !hasAllowedList ||
    allowedInsurances.includes("all") ||
    BONZAH_INSURANCE_IDS.some((id) => allowedInsurances.includes(id));
  const isInsuranceAllowedOnFleet = (insurance: any) =>
    !hasAllowedList ||
    allowedInsurances.includes("all") ||
    (bonzahCoverageValues.has(insurance) || bonzahAddonValues.has(insurance)
      ? fleetAllowsBonzah
      : allowedInsurances.includes(insurance));

  useEffect(() => {
    if (selectedCar?.extras) {
      const requiredExtras = selectedCar.extras
        .filter((extra: any) => extra.is_required)
        .map((extra: any) => extra.id);

      setSelectedExtras((prevSelectedExtras: Set<any>) => {
        const updated = new Set(prevSelectedExtras);
        requiredExtras.forEach((id) => updated.add(id));
        return updated;
      });
    }
  }, [selectedCar?.extras, setSelectedExtras]);

  useEffect(() => {
    if (
      selectedCar &&
      Array.isArray(selectedCar.allowed_insurances) &&
      Array.isArray(selectedBonzahProducts) &&
      selectedBonzahProducts.length > 0 &&
      // Wait for the Bonzah catalog so coverage codes resolve; running before it
      // loads would treat every Bonzah code as "not allowed" and strip it.
      (bonzahProducts?.insurance_options?.length ?? 0) > 0
    ) {
      if (selectedCar.allowed_insurances.includes("all")) {
        return;
      }
      const filteredInsurances = selectedBonzahProducts.filter((insurance) =>
        isInsuranceAllowedOnFleet(insurance)
      );
      if (filteredInsurances.length !== selectedBonzahProducts.length) {
        // Keep selection a FLAT array (1NOW-688/1NOW-1206): a Set here would
        // fail the Array.isArray guards below and blank the checkboxes.
        setSelectedBonzahProducts(filteredInsurances as any);
      }
    }
  }, [selectedCar, selectedBonzahProducts, setSelectedBonzahProducts, bonzahProducts]);

  // 1NOW-1421: when the whole booking is in the past, strip only the Bonzah
  // coverage + add-on codes from the selection (leaving canopy/manual intact) so
  // Bonzah can't be submitted for an all-past booking — the section shows a
  // blocking error instead.
  useEffect(() => {
    if (!stripPastBonzah) return;
    if (!Array.isArray(selectedBonzahProducts) || selectedBonzahProducts.length === 0)
      return;
    if ((bonzahProducts?.insurance_options?.length ?? 0) === 0) return;
    const bonzahCodes = new Set<any>();
    bonzahPrimaryProducts.forEach((p: any) =>
      productValues(p).forEach((v: any) => bonzahCodes.add(v))
    );
    (bonzahProducts?.addons ?? []).forEach((a: any) =>
      productValues(a).forEach((v: any) => bonzahCodes.add(v))
    );
    const filtered = selectedBonzahProducts.filter(
      (code: any) => !bonzahCodes.has(code)
    );
    if (filtered.length !== selectedBonzahProducts.length) {
      setSelectedBonzahProducts(filtered as any);
    }
  }, [stripPastBonzah, selectedBonzahProducts, bonzahProducts, setSelectedBonzahProducts]);
  useEffect(() => {
    if (selectedCar?.extras && selectedExtras && selectedExtras.size > 0) {
      const availableExtraIds = selectedCar.extras.map(
        (extra: any) => extra.id
      );
      let needsUpdate = false;
      const filteredExtras = new Set<any>();
      selectedExtras.forEach((extraId: number) => {
        if (availableExtraIds.includes(extraId)) {
          filteredExtras.add(extraId);
        } else {
          needsUpdate = true;
        }
      });
      if (needsUpdate) {
        setSelectedExtras(filteredExtras);
      }
    }
  }, [selectedCar?.extras, selectedExtras, setSelectedExtras]);

  // 1NOW-714: a booking made with Bonzah must keep Bonzah when edited — the
  // renter cannot swap it for another (non-Bonzah/"test") insurance option.
  // Detect whether the booking's selected options include a real Bonzah
  // coverage (bonzahCoverageValues is computed above, alongside the
  // fleet-allows-Bonzah resolver).
  const bookingHasBonzah =
    Array.isArray(bookingDetails?.bonzah_insurance_options) &&
    bookingDetails.bonzah_insurance_options.some((option: any) =>
      bonzahCoverageValues.has(option)
    );

  const insuranceNotAllowed =
    Array.isArray(bookingDetails?.bonzah_insurance_options) &&
    Array.isArray(selectedCar?.allowed_insurances) &&
    selectedCar &&
    !selectedCar.allowed_insurances.includes("all") &&
    bookingDetails.bonzah_insurance_options.some(
      (id: any) => !isInsuranceAllowedOnFleet(id)
    );

  const extrasNotAllowed =
    Array.isArray(bookingDetails?.extras) &&
    bookingDetails.extras.length > 0 &&
    Array.isArray(selectedCar?.extras) &&
    selectedCar &&
    bookingDetails.extras.some(
      (extra: any) => !selectedCar.extras.find((e: any) => e.id === extra.id)
    );

  const showBanner = insuranceNotAllowed || extrasNotAllowed;

  const renderBonzahSection = () => {
    return (
      <>
        <div className="listingSection__wrap">
          <h2 className="text-2xl text-text-color font-bold">
            Trip Insurance
          </h2>
          {bookingHasBonzah && (
            <p className="text-sm text-text-color/70">
              This booking includes Bonzah insurance. You can change your Bonzah
              coverage below, but cannot switch to a non-Bonzah option.
            </p>
          )}
          {stripPastBonzah && (
            <div className="border border-red-200 bg-red-50 rounded-md p-4 text-sm text-red-700">
              Bonzah cannot be added because all booking days are in the past.
            </div>
          )}
          {bonzahProducts?.insurance_options
            // 1NOW-1599: Bonzah renders on edit regardless of trip length —
            // >30-day coverage is purchased as multiple ≤30-day split policies
            // on the backend (1NOW-681), same as the FMS.
            // 1NOW-1421: block Bonzah coverages when the whole booking is past.
            ?.filter(
              (product: any) =>
                !(allDaysInPast && product.insurance_by_bonzah === true)
            )
            .map((product: any) => {
              const isBonzahProduct = product?.insurance_by_bonzah === true;
              // 1NOW-714: when the booking has Bonzah, keep Bonzah swappable but
              // block switching to a non-Bonzah/"test" option (which would drop
              // Bonzah). Non-Bonzah options stay editable when there's no Bonzah.
              const productLocked = bookingHasBonzah && !isBonzahProduct;
              return (
              <div
                key={product.title}
                className="flex flex-col justify-between items-start border border-secondary-100 rounded-lg p-4  max-w-full overflow-hidden"
                style={{ wordBreak: "break-word" }}
              >
                <div className="flex items-start">
                  <Checkbox
                    disabled={productLocked}
                    checked={isProductChecked(product)}
                    onChange={() => {
                      if (productLocked) return;
                      attemptBonzahProductSelection(product);
                    }}
                    className={`group size-7 rounded-md p-1 ring-1 ring-primary-200 ring-inset data-[checked]:bg-primary-200 data-[checked]:bg-opacity-20  mr-4 ${
                      productLocked ? "cursor-not-allowed opacity-50" : ""
                    }`}
                  >
                    <CheckIcon
                      className={`size-5 stroke-primary-200 ${
                        isProductChecked(product) ? "visible" : "invisible"
                      }`}
                    />
                  </Checkbox>

                  <div className="flex-1 min-w-0">
                    <div className="flex items-center mb-2">
                      <h2 className="text-lg md:text-xl font-bold text-text-color truncate">
                        {product.title}
                      </h2>
                      {product?.pdf !== "" && (
                        <button
                          className="ml-3 inline-flex items-center gap-1 rounded-md border border-secondary-100 px-2.5 py-1 text-xs font-medium text-text-color hover:bg-secondary-50"
                          onClick={() => window.open(product.pdf, "_blank")}
                          type="button"
                          tabIndex={-1}
                        >
                          View Coverage
                          <svg
                            xmlns="http://www.w3.org/2000/svg"
                            width="12"
                            height="12"
                            fill="none"
                            viewBox="0 0 24 24"
                            stroke="currentColor"
                            strokeWidth={2}
                          >
                            <path
                              strokeLinecap="round"
                              strokeLinejoin="round"
                              d="M13.5 6H5.25A2.25 2.25 0 003 8.25v10.5A2.25 2.25 0 005.25 21h10.5A2.25 2.25 0 0018 18.75V10.5m-10.5 6L21 3m0 0h-5.25M21 3v5.25"
                            />
                          </svg>
                        </button>
                      )}
                    </div>

                    {product?.options &&
                      Object.entries(product.options).map(([key, values]) => (
                        <div key={key} className="mt-2">
                          {!product.canopy && product.insurance_by_bonzah && (
                            <h3 className="font-semibold uppercase truncate">
                              {key}
                            </h3>
                          )}

                          {product.insurance_by_bonzah ? (
                            <ul className="list-disc pl-5 text-sm space-y-1 break-words">
                              {(Array.isArray(values) ? values : [values]).map(
                                (detail: any, index: number) => (
                                  <li
                                    key={index}
                                    style={{ wordBreak: "break-word" }}
                                  >
                                    {detail}
                                  </li>
                                )
                              )}
                            </ul>
                          ) : (
                            <p className="lg:text-lg text-md break-words">
                              {(Array.isArray(values) ? values : [values]).map(
                                (detail, index) => {
                                  if (
                                    typeof detail === "string" &&
                                    detail.trim().startsWith("<")
                                  ) {
                                    const urlRegex = /(https?:\/\/[^\s<>"']+)/g;
                                    let html = detail.replace(urlRegex, (url) => {
                                      if (detail.includes(`<a href="${url}`))
                                        return url;
                                      return `<a href="${url}" target="_blank" rel="noopener noreferrer" class="text-blue-500 hover:underline">${url}</a>`;
                                    });
                                    return (
                                      <span
                                        key={index}
                                        className={
                                          product.canopy
                                            ? "text-red-500 font-semibold"
                                            : ""
                                        }
                                        style={{ wordBreak: "break-word" }}
                                        dangerouslySetInnerHTML={{ __html: html }}
                                      />
                                    );
                                  }
                                  const urlRegex = /(https?:\/\/[^\s]+)/g;
                                  if (
                                    typeof detail === "string" &&
                                    urlRegex.test(detail)
                                  ) {
                                    const parts = detail.split(urlRegex);
                                    return (
                                      <span
                                        key={index}
                                        className={
                                          product.canopy
                                            ? "text-red-500 font-semibold"
                                            : ""
                                        }
                                        style={{ wordBreak: "break-word" }}
                                      >
                                        {parts.map((part, i) =>
                                          urlRegex.test(part) ? (
                                            <a
                                              key={i}
                                              href={part}
                                              target="_blank"
                                              rel="noopener noreferrer"
                                              className="text-blue-500 hover:underline"
                                              style={{ wordBreak: "break-word" }}
                                            >
                                              {part}
                                            </a>
                                          ) : (
                                            <React.Fragment key={i}>
                                              {part}
                                            </React.Fragment>
                                          )
                                        )}
                                        <br />
                                      </span>
                                    );
                                  }
                                  return (
                                    <span
                                      key={index}
                                      className={
                                        product.canopy
                                          ? "text-red-500 font-semibold"
                                          : ""
                                      }
                                      style={{ wordBreak: "break-word" }}
                                    >
                                      {detail}
                                      <br />
                                    </span>
                                  );
                                }
                              )}
                            </p>
                          )}
                        </div>
                      ))}
                  </div>
                </div>

                <div className="mt-4 md:mt-0 w-full flex justify-end items-center">
                  <p className="text-2xl font-bold text-text-color truncate">
                    ${product.price.toFixed(2)}
                    <span className="text-text-color lg:text-lg">
                      /
                      {product.nature === "per_trip"
                        ? "trip"
                        : product.nature === "per_day"
                        ? "day"
                        : "24 hours"}
                    </span>
                  </p>
                </div>
              </div>
              );
            })}
        </div>
      </>
    );
  };

  const renderExtrasSection = () => {
    return (
      <div className="listingSection__wrap mt-10">
        <div className="w-full flex flex-col sm:rounded-md border-b sm:border-t sm:border-l sm:border-r border-secondary-100  space-y-6  border-none">
          <h2 className="text-2xl font-bold text-text-color">
           Trip Extras
          </h2>

          {/* 1NOW-710: Bonzah add-on coverages, now selectable on edit (matches
              the create flow). 1NOW-1421: hidden when the booking is fully past. */}
          {!allDaysInPast &&
            (bonzahProducts?.addons ?? []).map((addon: any) => {
              const addonCode = Array.isArray(addon?.value)
                ? addon.value[0]
                : addon?.value;
              // 1NOW-1053: a code that belongs to the selected primary package
              // (e.g. Standard already includes "rcli") isn't an add-on
              // selection, so don't show that add-on as checked.
              const _primary = selectedPrimaryProduct();
              const checked =
                currentSelection().includes(addonCode) &&
                !(_primary && productValues(_primary).includes(addonCode));
              return (
                <div
                  key={addon.title}
                  className="w-full rounded-md border border-secondary-100 flex justify-between items-center p-5"
                >
                  <div className="flex justify-start flex-1 min-w-0">
                    <Checkbox
                      checked={checked}
                      onChange={() => toggleBonzahAddon(addon)}
                      className="group size-7 rounded-md p-1 ring-1 ring-primary-200 ring-inset data-[checked]:bg-primary-200 data-[checked]:bg-opacity-20 mr-4 flex-shrink-0"
                    >
                      <CheckIcon
                        className={`size-5 stroke-primary-200 ${
                          checked ? "visible" : "invisible"
                        }`}
                      />
                    </Checkbox>
                    <div className="min-w-0">
                      <div className="text-text-color lg:text-lg sm:text-l font-bold flex space-x-2 flex-wrap break-words">
                        <span className="truncate block max-w-xs text-text-color">
                          {addon.title}
                        </span>
                      </div>
                      {addon?.pdf ? (
                        <button
                          type="button"
                          className="mt-1 inline-flex items-center gap-1 rounded-md border border-secondary-100 px-2.5 py-1 text-xs font-medium text-text-color hover:bg-secondary-50"
                          onClick={() => window.open(addon.pdf, "_blank")}
                        >
                          View Coverage
                        </button>
                      ) : null}
                    </div>
                  </div>
                  <div className="mt-4 md:mt-0 flex-shrink-0 flex flex-col items-end">
                    <p className="lg:text-2xl text-lg font-bold text-text-color flex items-center whitespace-nowrap">
                      ${addon?.price}
                      <span className="text-text-color lg:text-lg">
                        {" "}
                        /
                        {addon?.nature === "per_trip"
                          ? "trip"
                          : addon?.nature === "per_day"
                          ? "day"
                          : "24 hours"}
                      </span>
                    </p>
                  </div>
                </div>
              );
            })}

          {(selectedCar?.extras ?? []).map((extra) => (
            <div
              key={extra.id}
              className="w-full rounded-md border border-secondary-100 flex justify-between items-center p-5"
            >
              <div className="flex justify-start flex-1 min-w-0">
                <Checkbox
                  checked={selectedExtras?.has(extra.id)}
                  onChange={(checked) => {
                    if (!extra.is_required) {
                      handleExtraChange(extra.id, checked);
                    }
                  }}
                  className="group size-7 rounded-md p-1 ring-1 ring-primary-200 ring-inset data-[checked]:bg-primary-200 data-[checked]:bg-opacity-20 mr-4 flex-shrink-0"
                  disabled={extra.is_required}
                >
                  <CheckIcon
                    className={`size-5 stroke-primary-200 ${
                      selectedExtras.has(extra.id) ? "visible" : "invisible"
                    }`}
                  />
                </Checkbox>
                <div className="min-w-0">
                  <div className="text-text-color lg:text-lg sm:text-l font-bold flex space-x-2 flex-wrap break-words">
                    <span className="truncate block max-w-xs text-text-color">
                      {extra.name}
                    </span>
                  </div>
                  <div className="text-text-color dark:text-neutral-400 lg:text-lg sm:text-md font-medium">
                    {renderDescriptionWithLink(extra.description)}
                  </div>
                </div>
              </div>

              <div className="mt-4 md:mt-0 flex-shrink-0 flex flex-col items-end">
                <div className="mb-2 flex space-x-2">
                  {extra.is_required && <Badge color="blue" name="Required" />}
                  {extra.is_recommended === true && (
                    <Badge color="blue" name="Recommended" />
                  )}
                </div>
                <p className="lg:text-2xl text-lg font-bold text-text-color flex items-center whitespace-nowrap">
                  ${extra?.amount}
                  <span className="text-text-color lg:text-lg">
                    {" "}
                    /{extra?.nature === "per_day" ? "day" : "trip"}
                  </span>
                </p>
              </div>
            </div>
          ))}
        </div>
      </div>
    );
  };

  return (
    <div>
      <div className="mt-12">
        {showBanner && (
          <div className="bg-yellow-100 border-l-4 border-yellow-500 text-yellow-700 p-4 mb-4 rounded">
            <div className="flex items-center">
              <svg
                className="h-6 w-6 text-yellow-500 mr-2"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M13 16h-1v-4h-1m1-4h.01M12 20h.01"
                />
              </svg>
              <span>
                Previously selected insurance and extras are unavailable for the
                new fleet. Please choose new options
              </span>
            </div>
          </div>
        )}
        {/* 1NOW-710: show the insurance section on edit whenever the fleet
            offers insurance — including bookings that had none initially, so
            the renter can add coverage while editing. */}
        {bonzahProducts?.insurance_options &&
          bonzahProducts.insurance_options.length > 0 &&
          renderBonzahSection()}
        {((selectedCar?.extras?.length ?? 0) > 0 ||
          (bonzahProducts?.addons?.length ?? 0) > 0) &&
          renderExtrasSection()}
      </div>

      {/* 1NOW-688: one-time Bonzah acknowledgement before a Bonzah coverage
          change on the website edit flow (mirrors the create/booking flow). */}
      <BonzahAcknowledgement
        isOpen={bonzahAckOpen}
        onClose={() => {
          setBonzahAckOpen(false);
          setPendingBonzahProduct(null);
        }}
        onConfirm={confirmBonzahAcknowledgement}
      />
    </div>
  );
};

export default InsuranceandExtras;